import { ensureRouteData } from '@/app/query-client'
import { LOCAL_AVAILABLE, PIPELINE_AVAILABLE } from '@/features/ipo/api/ipoAvailability'
import { issuesListQueryOptions } from '@/features/ipo/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { issueListStatusSchema, issueListTypeSchema, type IssueType } from '@/schemas/ipo'
import { tableQuerySchema } from '@/schemas/table'

/** Absent means the default type, IPO — the first page of the section strip. */
export const DEFAULT_TYPE: IssueType = 'ipo'

/**
 * Type, status and page live in the URL, like the Market tables. A hand-edited value falls back
 * rather than throwing. `local` (IPO-Local) and `pipeline` are the mock's two that mm-be does not
 * serve yet: they are valid here so their tab can say "Not available yet" (`ipoAvailability.ts`),
 * and the loader reads nothing for them. Moved here from the screen file, as `stockSearchSchema`
 * moved into `StockScreen.loader.ts`, so the loader below and the route's `validateSearch` read
 * the one schema.
 */
export const publicIssuesSearchSchema = tableQuerySchema.extend({
  type: issueListTypeSchema.optional().catch(undefined),
  status: issueListStatusSchema.default('open').catch('open'),
})

/**
 * The `/market/issues` loader.
 *
 * MAIN: the server-paged table read for the ENTRY URL (no `loaderDeps`): a status tab or a pager
 * click re-runs no loader, and the screen's hook keeps the previous rows
 * (`placeholderData: keepPreviousData`), so that change dims the table in place instead of
 * suspending the route. A view mm-be cannot serve (Pipeline, IPO-Local) reads nothing.
 */
export async function loadPublicIssues({
  context: { queryClient },
  location,
}: {
  context: RouterContext
  location: { search: unknown }
}): Promise<void> {
  const search = publicIssuesSearchSchema.parse(location.search)
  const type = search.type ?? DEFAULT_TYPE
  if (type === 'local' && !LOCAL_AVAILABLE) return
  if (search.status === 'pipeline' && !PIPELINE_AVAILABLE) return
  await ensureRouteData(
    queryClient,
    issuesListQueryOptions({
      type: type === 'local' ? DEFAULT_TYPE : type,
      status: search.status === 'pipeline' ? 'open' : search.status,
      page: search.page,
      pageSize: search.pageSize,
    }),
  )
}
